import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { IdentificationBadge, Plus, Trash, PencilSimple, ShieldCheck, Warning, X, Clock, CheckCircle, Prohibit, LinkSimple, Copy, FileArrowDown, Package } from "@phosphor-icons/react";
import Cip004R43Review from "../components/Cip004R43Review";
import { useModuleVisibility } from "../hooks/useModuleVisibility";

const TABS = [
  { id: "people", label: "People" },
  { id: "r43", label: "R4.3 Access Review" },
  { id: "requests", label: "Access Requests" },
  { id: "catalog", label: "Access Catalog" },
  { id: "verifications", label: "Verifications" },
  { id: "revocations", label: "Revocations" },
  { id: "rsaw", label: "RSAW Evidence" },
  { id: "overview", label: "Overview" },
];
const PERSON_TYPES = ["Employee", "Contractor", "Service Vendor"];
const STATUSES = ["active", "transferred", "terminated"];

const PRA_BADGE = {
  current: "bg-green-100 text-green-800 border-green-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  expired: "bg-red-100 text-red-800 border-red-300",
  none: "bg-gray-100 text-gray-600 border-gray-300",
};
const praLabel = (s) => ({ current: "Current", due_soon: "Renewal due", expired: "Expired", none: "No PRA" }[s] || s);

// Stateless render helpers hoisted to module scope (react/no-unstable-nested-components).
const RsawSection = ({ title, children }) => (
  <div className="border border-border mb-4">
    <div className="bg-secondary px-3 py-2 font-semibold text-sm">{title}</div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);
const RsawTbl = ({ head, rows, render, empty, tid }) => (
  <table className="text-sm min-w-full" data-testid={tid}>
    <thead className="bg-secondary/50"><tr>{head.map((h) => <th key={h} className="px-3 py-1.5 text-left font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
    <tbody>{rows.length === 0 ? <tr><td colSpan={head.length} className="px-3 py-4 text-center text-muted-foreground">{empty}</td></tr> : rows.map(render)}</tbody>
  </table>
);

const TRAIN_BADGE = {
  current: "bg-green-100 text-green-800 border-green-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  expired: "bg-red-100 text-red-800 border-red-300",
  none: "bg-gray-100 text-gray-600 border-gray-300",
};
const trainLabel = (s) => ({ current: "Current", due_soon: "Due soon", expired: "Overdue", none: "None" }[s] || s);

const SLA_BADGE = {
  overdue: "bg-red-100 text-red-800 border-red-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  on_track: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  completed_late: "bg-orange-100 text-orange-800 border-orange-300",
};
const slaLabel = (s) => ({ overdue: "Overdue", due_soon: "Due soon", on_track: "On track", completed: "Completed", completed_late: "Completed late" }[s] || s);

// Live "1d 04:12:33" / "-02:15:09" countdown from a due_at ISO string.
function fmtCountdown(dueIso) {
  if (!dueIso) return "—";
  const secs = Math.floor((new Date(dueIso).getTime() - Date.now()) / 1000);
  const neg = secs < 0;
  let s = Math.abs(secs);
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); const sec = s - m * 60;
  const pad = (n) => String(n).padStart(2, "0");
  const body = (d > 0 ? `${d}d ` : "") + `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return (neg ? "-" : "") + body;
}

export default function Cip004Access() {
  const { isRole } = useAuth();
  const canApprove = isRole("admin", "reviewer");
  const [tab, setTab] = useState("people");
  const { isSubHidden } = useModuleVisibility();
  const visibleTabs = TABS.filter((t) => !isSubHidden(`cip004:${t.id}`));
  useEffect(() => {
    if (isSubHidden(`cip004:${tab}`)) setTab(visibleTabs[0]?.id || "overview");
  }, [tab, visibleTabs, isSubHidden]);
  const [people, setPeople] = useState([]);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [vTypes, setVTypes] = useState([]);
  const [revocations, setRevocations] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [editing, setEditing] = useState(null);   // person form
  const [praFor, setPraFor] = useState(null);      // person for PRA drawer
  const [trainFor, setTrainFor] = useState(null);  // person for Training drawer
  const [showReq, setShowReq] = useState(false);
  const [openVerif, setOpenVerif] = useState(null);
  const [linkFor, setLinkFor] = useState(null);    // person for external link modal
  const [showPolicy, setShowPolicy] = useState(false);
  const csvRef = useRef(null);

  const importTraining = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cip004/training/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} training record(s)${data.unmatched?.length ? `, ${data.unmatched.length} unmatched` : ""}`);
      loadPeople(); loadSummary();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadPeople = useCallback(() => api.get("/cip004/people").then((r) => setPeople(r.data.people)).catch(() => {}), []);
  const loadRequests = useCallback(() => api.get("/cip004/access-requests").then((r) => setRequests(r.data.requests)).catch(() => {}), []);
  const loadSummary = useCallback(() => api.get("/cip004/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const loadVerifications = useCallback(() => api.get("/cip004/verifications").then((r) => { setVerifications(r.data.verifications); setVTypes(r.data.types); }).catch(() => {}), []);
  const loadRevocations = useCallback(() => api.get("/cip004/revocations").then((r) => setRevocations(r.data.revocations)).catch(() => {}), []);
  const loadBundles = useCallback(() => api.get("/cip004/bundles").then((r) => setBundles(r.data.bundles)).catch(() => {}), []);

  useEffect(() => {
    loadPeople(); loadRequests(); loadSummary(); loadVerifications(); loadRevocations(); loadBundles();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.post("/cip004/pra/notify").catch(() => {});   // surface PRA deadline alerts
    api.post("/cip004/training/run-reminders").catch(() => {});   // R2 training reminders/escalations
  }, [loadPeople, loadRequests, loadSummary, loadVerifications, loadRevocations, loadBundles]);

  const delPerson = async (p) => {
    if (!window.confirm(`Delete ${p.name} and all their PRA records?`)) return;
    try { await api.delete(`/cip004/people/${p.id}`); toast.success("Deleted"); loadPeople(); loadSummary(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const initiateTermination = async (p) => {
    if (!window.confirm(`Initiate CIP-004 R5 access revocation for ${p.name}? This starts the 24-hour SLA clock and marks them terminated.`)) return;
    try {
      const { data } = await api.post(`/cip004/people/${p.id}/initiate-termination`);
      toast.success(`${data.created} revocation task(s) started`);
      loadPeople(); loadSummary(); loadRevocations(); setTab("revocations");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveReq = async (r) => {
    if (!window.confirm(`Approve & grant ${(r.access_kinds || []).join(", ")} access for ${r.subject_name}? Access is granted only after this approval.`)) return;
    try {
      await api.post(`/cip004/access-requests/${r.id}/approve`, { comment: "" });
      toast.success("Access approved & granted"); loadRequests(); loadSummary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const denyReq = async (r) => {
    const comment = window.prompt(`Deny access request for ${r.subject_name}? Optional reason:`, "");
    if (comment === null) return;
    try { await api.post(`/cip004/access-requests/${r.id}/deny`, { comment }); toast.success("Request denied"); loadRequests(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-full p-6" data-testid="cip004-page">
      <div className="flex items-center gap-2 mb-1">
        <IdentificationBadge size={26} weight="duotone" />
        <h1 className="text-2xl font-bold">CIP-004 Access Management</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Workforce registry, personnel risk assessments (PRA), and access authorization — CIP-004-7 R3/R4.</p>

      <div className="flex gap-1 border-b border-border mb-4" data-testid="cip004-tabs">
        {visibleTabs.map((t) => (
          <button key={t.id} data-testid={`cip004-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "r43" && <Cip004R43Review />}

      {tab === "people" && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" className="gap-1" onClick={() => setEditing({})} data-testid="cip004-add-person-btn"><Plus size={14} /> Add Person</Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => setShowPolicy(true)} data-testid="cip004-training-policy-btn"><Clock size={14} /> Training policy</Button>
            <Button size="sm" variant="outline" className="gap-1" onClick={() => csvRef.current?.click()} data-testid="cip004-import-training-btn"><FileArrowDown size={14} /> Import training</Button>
            <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={importTraining} data-testid="cip004-import-training-input" />
            <span className="text-xs text-muted-foreground">{people.length} in registry</span>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="text-sm min-w-full" data-testid="cip004-people-table">
              <thead className="bg-secondary">
                <tr>{["Name", "Type", "Company", "Title", "Manager", "Status", "PRA", "Training", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {people.length === 0 ? (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip004-people-empty">No people yet. Add your workforce.</td></tr>
                ) : people.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`cip004-person-${p.id}`}>
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2">{p.person_type}</td>
                    <td className="px-3 py-2">{p.company}</td>
                    <td className="px-3 py-2">{p.title}</td>
                    <td className="px-3 py-2">{p.manager_name}</td>
                    <td className="px-3 py-2 capitalize">{p.status}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs border px-1.5 py-0.5 rounded ${PRA_BADGE[p.pra?.status] || ""}`} data-testid={`cip004-pra-badge-${p.id}`}>
                        {praLabel(p.pra?.status)}{p.pra?.valid_until ? ` · to ${p.pra.valid_until}` : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs border px-1.5 py-0.5 rounded ${TRAIN_BADGE[p.training?.status] || ""}`} data-testid={`cip004-train-badge-${p.id}`}>
                        {trainLabel(p.training?.status)}{p.training?.next_due ? ` · due ${p.training.next_due}` : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button className="text-xs text-blue-600 hover:underline mr-2" onClick={() => setPraFor(p)} data-testid={`cip004-pra-btn-${p.id}`}>PRA</button>
                      <button className="text-xs text-teal-600 hover:underline mr-2" onClick={() => setTrainFor(p)} data-testid={`cip004-train-btn-${p.id}`}>Training</button>
                      {p.status !== "terminated" && (
                        <button className="text-xs text-indigo-600 hover:underline mr-2" onClick={() => setLinkFor(p)} data-testid={`cip004-link-${p.id}`}>Share link</button>
                      )}
                      {p.status !== "terminated" && (
                        <button className="text-xs text-red-600 hover:underline mr-2" onClick={() => initiateTermination(p)} data-testid={`cip004-terminate-${p.id}`}>Terminate</button>
                      )}
                      <button className="text-muted-foreground hover:text-foreground mr-2" onClick={() => setEditing(p)} data-testid={`cip004-edit-${p.id}`}><PencilSimple size={14} /></button>
                      <button className="text-muted-foreground hover:text-red-600" onClick={() => delPerson(p)} data-testid={`cip004-del-${p.id}`}><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "requests" && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" className="gap-1" onClick={() => setShowReq(true)} data-testid="cip004-new-request-btn"><Plus size={14} /> New Access Request</Button>
            <span className="text-xs text-muted-foreground">{requests.length} request(s)</span>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="text-sm min-w-full" data-testid="cip004-requests-table">
              <thead className="bg-secondary">
                <tr>{["Subject", "Access", "Prerequisites", "Approver", "Status", "Requested", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip004-requests-empty">No access requests yet.</td></tr>
                ) : requests.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top" data-testid={`cip004-request-${r.id}`}>
                    <td className="px-3 py-2 font-medium">{r.subject_name}{r.source === "external" && <span className="ml-1 text-[10px] border border-indigo-300 bg-indigo-50 text-indigo-700 px-1 rounded">ext</span>}</td>
                    <td className="px-3 py-2">{(r.access_kinds || []).join(", ")}<div className="text-xs text-muted-foreground max-w-xs truncate" title={r.business_reason}>{r.business_reason}</div></td>
                    <td className="px-3 py-2">
                      {r.status === "approved" || r.status === "denied" ? <span className="text-xs text-muted-foreground">—</span> : r.prereqs ? (
                        <div className="flex flex-col gap-0.5" data-testid={`cip004-req-prereq-${r.id}`}>
                          <span className={`text-[11px] border px-1 rounded w-fit ${PRA_BADGE[r.prereqs.pra_status] || ""}`}>PRA: {praLabel(r.prereqs.pra_status)}</span>
                          <span className={`text-[11px] border px-1 rounded w-fit ${TRAIN_BADGE[r.prereqs.training_status] || ""}`}>Training: {trainLabel(r.prereqs.training_status)}</span>
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2">{r.asset_owner_user || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs border px-1.5 py-0.5 rounded ${r.status === "approved" ? "bg-green-100 text-green-800 border-green-300" : r.status === "denied" ? "bg-red-100 text-red-800 border-red-300" : "bg-blue-100 text-blue-800 border-blue-300"}`} data-testid={`cip004-req-status-${r.id}`}>
                        {r.status === "approved" ? "Approved / Granted" : r.status === "denied" ? "Denied" : "Requested"}
                      </span>
                      {r.approved_by && <div className="text-[11px] text-muted-foreground">by {r.approved_by} · {(r.approved_at || "").slice(0, 10)}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {canApprove && r.status === "requested" && (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => approveReq(r)} data-testid={`cip004-req-approve-${r.id}`}><ShieldCheck size={13} /> Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => denyReq(r)} data-testid={`cip004-req-deny-${r.id}`}>Deny</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "catalog" && (
        <AccessCatalogTab bundles={bundles} onReload={loadBundles} />
      )}

      {tab === "verifications" && (
        <VerificationsTab verifications={verifications} vTypes={vTypes} onReload={() => { loadVerifications(); loadSummary(); }} onOpen={setOpenVerif} />
      )}

      {tab === "revocations" && (
        <RevocationsTab revocations={revocations} onReload={() => { loadRevocations(); loadSummary(); }} />
      )}

      {tab === "rsaw" && (
        <RsawEvidenceTab />
      )}

      {tab === "overview" && summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cip004-overview">
          <Card title="Workforce">
            <Row label="Active" value={summary.by_status?.active || 0} />
            <Row label="Transferred" value={summary.by_status?.transferred || 0} />
            <Row label="Terminated" value={summary.by_status?.terminated || 0} />
          </Card>
          <Card title="PRA Status">
            <Row label="Current" value={summary.pra_counts?.current || 0} />
            <Row label="Renewal due" value={summary.pra_counts?.due_soon || 0} tone="text-amber-600" />
            <Row label="Expired" value={summary.pra_counts?.expired || 0} tone="text-red-600" />
            <Row label="No PRA" value={summary.pra_counts?.none || 0} />
          </Card>
          <Card title="Training Status (R2)">
            <Row label="Current" value={summary.training_counts?.current || 0} />
            <Row label="Due soon" value={summary.training_counts?.due_soon || 0} tone="text-amber-600" />
            <Row label="Overdue" value={summary.training_counts?.expired || 0} tone="text-red-600" />
            <Row label="No training" value={summary.training_counts?.none || 0} />
          </Card>
          <Card title="Access Requests">
            <Row label="Open" value={summary.access_requests_open || 0} />
          </Card>
          <Card title="Revocation SLA (R5)">
            <Row label="Overdue" value={summary.revocations?.overdue || 0} tone="text-red-600" />
            <Row label="Due soon" value={summary.revocations?.due_soon || 0} tone="text-amber-600" />
            <Row label="On track" value={summary.revocations?.on_track || 0} />
            <Row label="Completed late" value={summary.revocations?.completed_late || 0} tone="text-orange-600" />
          </Card>
          <Card title="Verification Cadence">
            {(summary.verification_cadence || []).map((v) => (
              <div key={v.type} className="py-1 text-sm border-b border-border/50 last:border-0" data-testid={`cip004-cadence-${v.type}`}>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{v.label}</span>
                  <span className={`font-semibold ${v.overdue ? "text-red-600" : ""}`}>{v.next_due ? `due ${v.next_due}` : "not started"}</span>
                </div>
                <div className="text-xs text-muted-foreground">{v.citation} · every {v.cadence_months} mo{v.last_completed ? ` · last ${v.last_completed}` : ""}</div>
              </div>
            ))}
          </Card>
          <div className="md:col-span-3 border border-border p-4">
            <div className="font-semibold mb-2 flex items-center gap-1"><Warning size={16} className="text-amber-600" /> PRA attention needed</div>
            {(summary.pra_due || []).length === 0 ? <p className="text-sm text-muted-foreground">All active personnel have a current PRA.</p> : (
              <ul className="text-sm space-y-1" data-testid="cip004-pra-due-list">
                {summary.pra_due.map((d) => (
                  <li key={d.person_id}>
                    <span className={`text-xs border px-1.5 py-0.5 rounded mr-2 ${PRA_BADGE[d.pra_status]}`}>{praLabel(d.pra_status)}</span>
                    {d.name} — valid until {d.valid_until || "N/A"} (renew from {d.renewal_due || "N/A"})
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {editing !== null && <PersonForm person={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); loadPeople(); loadSummary(); }} people={people} />}
      {praFor && <PraDrawer person={praFor} onClose={() => setPraFor(null)} onChanged={() => { loadPeople(); loadSummary(); }} />}
      {trainFor && <TrainingDrawer person={trainFor} onClose={() => setTrainFor(null)} onChanged={() => { loadPeople(); loadSummary(); }} />}
      {showReq && <RequestForm people={people} users={users} bundles={bundles} onClose={() => setShowReq(false)} onSaved={() => { setShowReq(false); loadRequests(); }} />}
      {openVerif && <VerificationDetail campaignId={openVerif} onClose={() => setOpenVerif(null)} onChanged={() => { loadVerifications(); loadSummary(); }} />}
      {linkFor && <RequestLinkModal person={linkFor} users={users} onClose={() => setLinkFor(null)} />}
      {showPolicy && <TrainingPolicyModal onClose={() => setShowPolicy(false)} onSaved={() => { setShowPolicy(false); loadPeople(); loadSummary(); }} />}
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="border border-border p-4"><div className="overline text-muted-foreground mb-2">{title}</div>{children}</div>
);
const Row = ({ label, value, tone }) => (
  <div className="flex justify-between py-0.5 text-sm"><span className="text-muted-foreground">{label}</span><span className={`font-semibold ${tone || ""}`}>{value}</span></div>
);

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={onClose}>
      <div className={`bg-card border border-border w-full ${wide ? "max-w-2xl" : "max-w-lg"} mx-4`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block text-sm mb-3"><span className="block font-medium mb-1">{label}</span>{children}</label>
);
const inputCls = "w-full border border-border rounded px-2 py-1.5 text-sm";

function PersonForm({ person, onClose, onSaved, people }) {
  const [f, setF] = useState({
    name: "", person_type: "Employee", company: "", title: "",
    manager_person_id: "", status: "active", start_date: "", email: "", notes: "", ...person,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const body = { ...f, manager_person_id: f.manager_person_id || null };
    try {
      if (person.id) await api.patch(`/cip004/people/${person.id}`, body);
      else await api.post("/cip004/people", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={person.id ? "Edit Person" : "Add Person"} onClose={onClose}>
      <Field label="Full name"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cip004-person-name" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><select className={inputCls} value={f.person_type} onChange={(e) => set("person_type", e.target.value)} data-testid="cip004-person-type">{PERSON_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="Status"><select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="cip004-person-status">{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Company"><input className={inputCls} value={f.company} onChange={(e) => set("company", e.target.value)} /></Field>
        <Field label="Position / Title"><input className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
        <Field label="Manager"><select className={inputCls} value={f.manager_person_id || ""} onChange={(e) => set("manager_person_id", e.target.value)} data-testid="cip004-person-manager"><option value="">—</option>{people.filter((p) => p.id !== person.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="Start date"><input type="date" className={inputCls} value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Field>
      </div>
      <Field label="Email"><input className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} data-testid="cip004-person-save">Save</Button></div>
    </Modal>
  );
}

function PraDrawer({ person, onClose, onChanged }) {
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get(`/cip004/people/${person.id}/pra`).then((r) => { setRecords(r.data.records); setStatus(r.data.pra_status); }).catch(() => {}), [person.id]);
  useEffect(() => { load(); }, [load]);

  const del = async (rid) => { try { await api.delete(`/cip004/pra/${rid}`); load(); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <Modal title={`PRA / Background Checks — ${person.name}`} onClose={onClose} wide>
      {status && (
        <div className="mb-3 text-sm">
          Current status: <span className={`text-xs border px-1.5 py-0.5 rounded ${PRA_BADGE[status.status]}`}>{praLabel(status.status)}</span>
          {status.valid_until && <> · valid until <b>{status.valid_until}</b> · renew from <b>{status.renewal_due}</b></>}
        </div>
      )}
      {!adding && <Button size="sm" className="gap-1 mb-3" onClick={() => setAdding(true)} data-testid="cip004-add-pra-btn"><Plus size={14} /> Record PRA</Button>}
      {adding && <PraForm personId={person.id} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); onChanged(); }} />}
      <div className="border border-border divide-y divide-border" data-testid="cip004-pra-records">
        {records.length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground">No PRA records.</div> : records.map((r) => (
          <div key={r.id} className="px-3 py-2 text-sm flex items-start justify-between gap-2">
            <div>
              <div className="font-medium">{r.completed_date || "—"} · {r.pra_type} {r.authorized ? "· ✅ authorized" : "· ⚠️ not authorized"}</div>
              <div className="text-xs text-muted-foreground">
                Identity: {r.identity_confirmed ? "confirmed" : "no"} · 7-yr criminal: {r.seven_year_completed ? "yes" : "no"}{r.partial ? ` (partial: ${r.partial_reason})` : ""} · valid to {r.valid_until || "N/A"}
              </div>
            </div>
            <button className="text-muted-foreground hover:text-red-600" onClick={() => del(r.id)} data-testid={`cip004-pra-del-${r.id}`}><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function PraForm({ personId, onClose, onSaved }) {
  const [f, setF] = useState({
    person_id: personId, completed_date: "", pra_type: "initial",
    identity_confirmed: false, identity_method: "", seven_year_completed: false,
    current_residence: "", other_locations: "", partial: false, partial_reason: "",
    criminal_eval_result: "", evaluated_by: "", authorized: false,
    contractor_verification: false, contractor_verification_notes: "", notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const chk = (k, label) => (
    <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`cip004-pra-${k}`} /> {label}</label>
  );
  const save = async () => {
    if (!f.completed_date) return toast.error("Completed date is required");
    try { await api.post("/cip004/pra", f); toast.success("PRA recorded"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-border p-3 mb-3 bg-secondary/30">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Completed date"><input type="date" className={inputCls} value={f.completed_date} onChange={(e) => set("completed_date", e.target.value)} data-testid="cip004-pra-completed" /></Field>
        <Field label="Type"><select className={inputCls} value={f.pra_type} onChange={(e) => set("pra_type", e.target.value)}><option value="initial">Initial</option><option value="renewal">Renewal</option></select></Field>
      </div>
      {chk("identity_confirmed", "Identity confirmed (R3.1)")}
      <Field label="Identity method"><input className={inputCls} value={f.identity_method} onChange={(e) => set("identity_method", e.target.value)} /></Field>
      {chk("seven_year_completed", "Full 7-year criminal history completed (R3.2)")}
      <Field label="Current residence (R3.2.1)"><input className={inputCls} value={f.current_residence} onChange={(e) => set("current_residence", e.target.value)} /></Field>
      <Field label="Other locations resided 6+ months in prior 7 yrs (R3.2.2)"><textarea className={inputCls} rows={2} value={f.other_locations} onChange={(e) => set("other_locations", e.target.value)} /></Field>
      {chk("partial", "Partial check (full 7-yr not possible)")}
      {f.partial && <Field label="Reason full 7-yr check could not be performed"><textarea className={inputCls} rows={2} value={f.partial_reason} onChange={(e) => set("partial_reason", e.target.value)} /></Field>}
      <Field label="Criminal history evaluation result (R3.3)"><input className={inputCls} value={f.criminal_eval_result} onChange={(e) => set("criminal_eval_result", e.target.value)} /></Field>
      <Field label="Evaluated by"><input className={inputCls} value={f.evaluated_by} onChange={(e) => set("evaluated_by", e.target.value)} /></Field>
      {chk("contractor_verification", "Contractor/vendor PRA verified per R3.1–3.3 (R3.4)")}
      {f.contractor_verification && <Field label="Verification notes"><input className={inputCls} value={f.contractor_verification_notes} onChange={(e) => set("contractor_verification_notes", e.target.value)} /></Field>}
      {chk("authorized", "Authorized for access based on this PRA")}
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} data-testid="cip004-pra-save">Save PRA</Button></div>
    </div>
  );
}

function RequestForm({ people, users, bundles, onClose, onSaved }) {
  const [f, setF] = useState({
    subject_person_id: "", requested_by_person_id: "", asset_owner_user: "",
    access_electronic: false, access_physical: false, access_bcsi: false,
    targets: "", business_reason: "", exceptional_circumstance: false, notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const applyBundle = (bid) => {
    const b = (bundles || []).find((x) => x.id === bid);
    if (!b) return;
    setF((p) => ({ ...p, access_electronic: p.access_electronic || b.access_electronic,
      access_physical: p.access_physical || b.access_physical, access_bcsi: p.access_bcsi || b.access_bcsi,
      targets: p.targets || b.targets || "" }));
    toast.success(`Applied bundle: ${b.name}`);
  };
  const subject = people.find((p) => p.id === f.subject_person_id);
  const praStatus = subject?.pra?.status;
  const trainStatus = subject?.training?.status;
  const praOk = praStatus === "current" || praStatus === "due_soon";
  const trainOk = trainStatus === "current" || trainStatus === "due_soon";
  const save = async () => {
    if (!f.subject_person_id) return toast.error("Select a subject");
    if (!(f.access_electronic || f.access_physical || f.access_bcsi)) return toast.error("Select at least one access type");
    if (!f.business_reason.trim()) return toast.error("Business reason is required");
    try { await api.post("/cip004/access-requests", { ...f, requested_by_person_id: f.requested_by_person_id || null }); toast.success("Access request created & routed"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title="New Access Request (CIP-004-7 R4.1)" onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Subject (direct report)"><select className={inputCls} value={f.subject_person_id} onChange={(e) => set("subject_person_id", e.target.value)} data-testid="cip004-req-subject"><option value="">—</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="Requesting manager"><select className={inputCls} value={f.requested_by_person_id} onChange={(e) => set("requested_by_person_id", e.target.value)} data-testid="cip004-req-manager"><option value="">—</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
      </div>
      {subject && (
        <div className={`text-xs border px-2 py-1.5 rounded mb-3 ${praOk && trainOk ? "bg-green-50 border-green-300 text-green-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid="cip004-req-pra-note">
          Prerequisites for {subject.name}: PRA <b>{praLabel(praStatus)}</b> · Training <b>{trainLabel(trainStatus)}</b>
          {!(praOk && trainOk) && <> — you can submit now, but the asset owner cannot grant access until both are current (R2 &amp; R3).</>}
        </div>
      )}
      <div className="mb-3">
        <span className="block text-sm font-medium mb-1">Access requested</span>
        {(bundles || []).length > 0 && (
          <div className="mb-2">
            <select className={`${inputCls} w-auto`} defaultValue="" onChange={(e) => { applyBundle(e.target.value); e.target.value = ""; }} data-testid="cip004-req-bundle">
              <option value="">Apply a role bundle…</option>
              {bundles.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_electronic} onChange={(e) => set("access_electronic", e.target.checked)} data-testid="cip004-req-electronic" /> Electronic access</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_physical} onChange={(e) => set("access_physical", e.target.checked)} data-testid="cip004-req-physical" /> Unescorted physical (PSP)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_bcsi} onChange={(e) => set("access_bcsi", e.target.checked)} data-testid="cip004-req-bcsi" /> BCSI</label>
      </div>
      <Field label="Target system(s) / PSP / BCSI"><input className={inputCls} value={f.targets} onChange={(e) => set("targets", e.target.value)} placeholder="e.g., EMS at Primary Control Center" /></Field>
      <Field label="Business reason (required)"><textarea className={inputCls} rows={2} value={f.business_reason} onChange={(e) => set("business_reason", e.target.value)} data-testid="cip004-req-reason" /></Field>
      <Field label="Approver — asset owner (ClaroShield user)"><select className={inputCls} value={f.asset_owner_user} onChange={(e) => set("asset_owner_user", e.target.value)} data-testid="cip004-req-owner"><option value="">—</option>{users.map((u) => <option key={u.id} value={u.name}>{u.name} ({u.role})</option>)}</select></Field>
      <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={f.exceptional_circumstance} onChange={(e) => set("exceptional_circumstance", e.target.checked)} data-testid="cip004-req-cec" /> Flag as CIP Exceptional Circumstance (documented for the approver — does not bypass the PRA under CIP-004-7)</label>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" className="gap-1" onClick={save} data-testid="cip004-req-save"><ShieldCheck size={14} /> Create & Route</Button></div>
    </Modal>
  );
}


// ---------------- Verification Campaigns (Phase 2) ----------------
function VerificationsTab({ verifications, vTypes, onReload, onOpen }) {
  const [newType, setNewType] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!newType) return toast.error("Pick a campaign type");
    setBusy(true);
    try { const { data } = await api.post("/cip004/verifications", { type: newType }); toast.success("Campaign created"); setNewType(""); onReload(); onOpen(data.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (v) => {
    if (!window.confirm("Delete this verification campaign?")) return;
    try { await api.delete(`/cip004/verifications/${v.id}`); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="cip004-verifications">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select className={`${inputCls} w-auto`} value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="cip004-verif-type">
          <option value="">Select campaign type…</option>
          {vTypes.map((t) => <option key={t.type} value={t.type}>{t.label} ({t.citation})</option>)}
        </select>
        <Button size="sm" className="gap-1" onClick={create} disabled={busy} data-testid="cip004-verif-create-btn"><Plus size={14} /> New Campaign</Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="text-sm min-w-full" data-testid="cip004-verif-table">
          <thead className="bg-secondary"><tr>{["Campaign", "Citation", "Opened", "Due", "Progress", "Discrepancies", "Status", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {verifications.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip004-verif-empty">No verification campaigns yet. Start a quarterly or 15-month review.</td></tr>
            ) : verifications.map((v) => (
              <tr key={v.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer" data-testid={`cip004-verif-${v.id}`} onClick={() => onOpen(v.id)}>
                <td className="px-3 py-2 font-medium">{v.type_label}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{v.citation}</td>
                <td className="px-3 py-2 whitespace-nowrap">{(v.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.due_date || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{v.progress?.reviewed || 0}/{v.progress?.total || 0}</td>
                <td className="px-3 py-2">{v.progress?.discrepancies ? <span className="text-red-600 font-semibold">{v.progress.discrepancies}</span> : 0}</td>
                <td className="px-3 py-2"><span className={`text-xs border px-1.5 py-0.5 rounded ${v.status === "completed" ? SLA_BADGE.completed : SLA_BADGE.on_track}`}>{v.status}</span></td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><button className="text-muted-foreground hover:text-red-600" onClick={() => del(v)} data-testid={`cip004-verif-del-${v.id}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VerificationDetail({ campaignId, onClose, onChanged }) {
  const [v, setV] = useState(null);
  const load = useCallback(() => api.get(`/cip004/verifications/${campaignId}`).then((r) => setV(r.data)).catch(() => {}), [campaignId]);
  useEffect(() => { load(); }, [load]);

  const review = async (personId, result, note = "") => {
    try { const { data } = await api.patch(`/cip004/verifications/${campaignId}/items/${personId}`, { result, note }); setV(data); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const complete = async () => {
    try { const { data } = await api.post(`/cip004/verifications/${campaignId}/complete`); setV(data); onChanged(); toast.success("Campaign completed & logged"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!v) return <Modal title="Verification Campaign" onClose={onClose} wide><div className="text-sm text-muted-foreground">Loading…</div></Modal>;
  const closed = v.status === "completed";
  return (
    <Modal title={`${v.type_label} — ${v.citation}`} onClose={onClose} wide>
      <div className="flex items-center justify-between mb-3 text-sm flex-wrap gap-2">
        <span className="text-muted-foreground">Opened {(v.created_at || "").slice(0, 10)} · Due {v.due_date || "—"} · {v.progress?.reviewed || 0}/{v.progress?.total || 0} reviewed</span>
        {!closed && <Button size="sm" className="gap-1" onClick={complete} data-testid="cip004-verif-complete-btn"><CheckCircle size={14} /> Complete campaign</Button>}
        {closed && <span className={`text-xs border px-2 py-0.5 rounded ${SLA_BADGE.completed}`}>Completed {(v.completed_at || "").slice(0, 10)}</span>}
      </div>
      <div className="border border-border divide-y divide-border max-h-[60vh] overflow-auto" data-testid="cip004-verif-items">
        {(v.items || []).length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground">No personnel matched this campaign's scope.</div> : (v.items || []).map((it) => (
          <div key={it.person_id} className="px-3 py-2 flex items-start justify-between gap-3" data-testid={`cip004-verif-item-${it.person_id}`}>
            <div className="min-w-0">
              <div className="font-medium text-sm">{it.person_name}</div>
              <div className="text-xs text-muted-foreground">Access: {it.access_summary}{it.reviewed_by ? ` · reviewed by ${it.reviewed_by}` : ""}</div>
              {it.result === "discrepancy" && it.note && <div className="text-xs text-red-600 mt-0.5">Discrepancy: {it.note}</div>}
            </div>
            {!closed ? (
              <div className="flex items-center gap-1 shrink-0">
                <button className={`text-xs border px-1.5 py-0.5 rounded ${it.result === "verified" ? SLA_BADGE.completed : "border-border text-muted-foreground hover:bg-secondary"}`} onClick={() => review(it.person_id, "verified")} data-testid={`cip004-verif-verify-${it.person_id}`}>Verified</button>
                <button className={`text-xs border px-1.5 py-0.5 rounded ${it.result === "discrepancy" ? SLA_BADGE.overdue : "border-border text-muted-foreground hover:bg-secondary"}`} onClick={() => { const note = window.prompt("Describe the discrepancy (access to remove / correction needed):", it.note || ""); if (note !== null) review(it.person_id, "discrepancy", note); }} data-testid={`cip004-verif-disc-${it.person_id}`}>Discrepancy</button>
              </div>
            ) : (
              <span className={`text-xs border px-1.5 py-0.5 rounded shrink-0 ${it.result === "verified" ? SLA_BADGE.completed : it.result === "discrepancy" ? SLA_BADGE.overdue : PRA_BADGE.none}`}>{it.result}</span>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ---------------- Revocation SLA (Phase 3) ----------------
function RevocationsTab({ revocations, onReload }) {
  const [, setTick] = useState(0);
  const timer = useRef(null);
  useEffect(() => { timer.current = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(timer.current); }, []);

  const complete = async (r) => {
    const note = window.prompt(`Confirm completion of: ${r.label}\nOptional note (how/where revoked):`, "");
    if (note === null) return;
    try { await api.post(`/cip004/revocations/${r.id}/complete`, { note }); toast.success("Marked complete"); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const open = revocations.filter((r) => !r.completed);
  const done = revocations.filter((r) => r.completed);

  return (
    <div data-testid="cip004-revocations">
      <p className="text-sm text-muted-foreground mb-3">CIP-004-7 R5 — high-risk access is revoked within a strict 24-hour clock; shared-account / reassignment tasks within 30 days. Terminating or transferring a person auto-creates these; countdowns are live.</p>
      <div className="border border-border overflow-x-auto mb-6">
        <table className="text-sm min-w-full" data-testid="cip004-rev-open-table">
          <thead className="bg-secondary"><tr>{["Person", "Task", "Citation", "Trigger", "Due", "Countdown", "SLA", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {open.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip004-rev-empty">No open revocation tasks. Terminate/transfer a person to start the SLA clock.</td></tr>
            ) : open.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`cip004-rev-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.person_name}</td>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.citation}</td>
                <td className="px-3 py-2 capitalize">{r.trigger}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{(r.due_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className={`px-3 py-2 font-mono whitespace-nowrap ${r.sla_state === "overdue" ? "text-red-600 font-bold" : r.sla_state === "due_soon" ? "text-amber-600" : ""}`} data-testid={`cip004-rev-countdown-${r.id}`}>{fmtCountdown(r.due_at)}</td>
                <td className="px-3 py-2"><span className={`text-xs border px-1.5 py-0.5 rounded ${SLA_BADGE[r.sla_state] || ""}`}>{slaLabel(r.sla_state)}</span></td>
                <td className="px-3 py-2"><Button size="sm" variant="outline" className="gap-1 h-7" onClick={() => complete(r)} data-testid={`cip004-rev-complete-${r.id}`}><CheckCircle size={13} /> Complete</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {done.length > 0 && (
        <div>
          <div className="font-semibold text-sm mb-2 flex items-center gap-1"><Prohibit size={15} /> Completed revocations ({done.length})</div>
          <div className="border border-border overflow-x-auto">
            <table className="text-sm min-w-full" data-testid="cip004-rev-done-table">
              <thead className="bg-secondary"><tr>{["Person", "Task", "Completed", "By", "SLA"].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {done.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`cip004-rev-done-${r.id}`}>
                    <td className="px-3 py-2 font-medium">{r.person_name}</td>
                    <td className="px-3 py-2">{r.label}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{(r.completed_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">{r.completed_by}</td>
                    <td className="px-3 py-2"><span className={`text-xs border px-1.5 py-0.5 rounded ${SLA_BADGE[r.sla_state] || ""}`}>{slaLabel(r.sla_state)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Access Catalog (Phase 4) ----------------
function AccessCatalogTab({ bundles, onReload }) {
  const [editing, setEditing] = useState(null);
  const del = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"?`)) return;
    try { await api.delete(`/cip004/bundles/${b.id}`); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const kinds = (b) => [b.access_electronic && "Electronic", b.access_physical && "Unescorted Physical", b.access_bcsi && "BCSI"].filter(Boolean).join(", ");
  return (
    <div data-testid="cip004-catalog">
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" className="gap-1" onClick={() => setEditing({})} data-testid="cip004-add-bundle-btn"><Plus size={14} /> New Role Bundle</Button>
        <span className="text-xs text-muted-foreground">{bundles.length} bundle(s)</span>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="text-sm min-w-full" data-testid="cip004-bundles-table">
          <thead className="bg-secondary"><tr>{["Bundle", "Access types", "Default targets", "Description", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {bundles.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground" data-testid="cip004-bundles-empty">No role bundles yet. Create reusable access roles (e.g., "EMS Operator").</td></tr>
            ) : bundles.map((b) => (
              <tr key={b.id} className="border-t border-border" data-testid={`cip004-bundle-${b.id}`}>
                <td className="px-3 py-2 font-medium flex items-center gap-1"><Package size={14} /> {b.name}</td>
                <td className="px-3 py-2">{kinds(b) || "—"}</td>
                <td className="px-3 py-2 max-w-xs truncate" title={b.targets}>{b.targets || "—"}</td>
                <td className="px-3 py-2 max-w-xs truncate text-muted-foreground" title={b.description}>{b.description || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button className="text-muted-foreground hover:text-foreground mr-2" onClick={() => setEditing(b)} data-testid={`cip004-bundle-edit-${b.id}`}><PencilSimple size={14} /></button>
                  <button className="text-muted-foreground hover:text-red-600" onClick={() => del(b)} data-testid={`cip004-bundle-del-${b.id}`}><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing !== null && <BundleForm bundle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onReload(); }} />}
    </div>
  );
}

function BundleForm({ bundle, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", access_electronic: false, access_physical: false, access_bcsi: false, targets: "", ...bundle });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("Bundle name is required");
    if (!(f.access_electronic || f.access_physical || f.access_bcsi)) return toast.error("Select at least one access type");
    const body = { name: f.name, description: f.description, access_electronic: f.access_electronic, access_physical: f.access_physical, access_bcsi: f.access_bcsi, targets: f.targets };
    try {
      if (bundle.id) await api.patch(`/cip004/bundles/${bundle.id}`, body);
      else await api.post("/cip004/bundles", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={bundle.id ? "Edit Role Bundle" : "New Role Bundle"} onClose={onClose}>
      <Field label="Bundle name"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g., EMS Operator" data-testid="cip004-bundle-name" /></Field>
      <Field label="Description"><input className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
      <div className="mb-3">
        <span className="block text-sm font-medium mb-1">Access types</span>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_electronic} onChange={(e) => set("access_electronic", e.target.checked)} data-testid="cip004-bundle-electronic" /> Electronic access</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_physical} onChange={(e) => set("access_physical", e.target.checked)} data-testid="cip004-bundle-physical" /> Unescorted physical (PSP)</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.access_bcsi} onChange={(e) => set("access_bcsi", e.target.checked)} data-testid="cip004-bundle-bcsi" /> BCSI</label>
      </div>
      <Field label="Default target systems / PSPs"><textarea className={inputCls} rows={2} value={f.targets} onChange={(e) => set("targets", e.target.value)} placeholder="e.g., EMS at Primary Control Center; PCC PSP" /></Field>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} data-testid="cip004-bundle-save">Save</Button></div>
    </Modal>
  );
}

// ---------------- RSAW Evidence Feed (Phase 4) ----------------
function RsawEvidenceTab() {
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/cip004/rsaw-evidence").then((r) => setEv(r.data)).catch(() => {}); }, []);

  const exportDocx = async () => {
    setBusy(true);
    try {
      const res = await api.get("/cip004/rsaw-evidence/export.docx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "CIP-004_RSAW_Evidence.docx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Evidence package exported");
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  if (!ev) return <div className="text-sm text-muted-foreground" data-testid="cip004-rsaw">Loading evidence…</div>;
  const m = ev.r5_metrics || {};
  const Section = RsawSection;
  const Tbl = RsawTbl;
  return (
    <div data-testid="cip004-rsaw">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-muted-foreground max-w-3xl">Audit-ready CIP-004-7 evidence: PRA roster (R3), access authorizations (R4.1), verification campaigns (R4.2–R4.4), and revocation SLA performance (R5). Export to Word for RSAW submission.</p>
        <Button size="sm" className="gap-1" onClick={exportDocx} disabled={busy} data-testid="cip004-rsaw-export-btn"><FileArrowDown size={14} /> {busy ? "Exporting…" : "Export Word"}</Button>
      </div>

      <Section title="R2 — Security & Cyber Security Training Roster">
        <Tbl tid="cip004-rsaw-r2" head={["Name", "Type", "Status", "Training", "Completed", "Next due"]} rows={ev.r2_training || []} empty="No personnel."
          render={(x) => <tr key={x.name} className="border-t border-border"><td className="px-3 py-1.5 font-medium">{x.name}</td><td className="px-3 py-1.5">{x.type}</td><td className="px-3 py-1.5 capitalize">{x.status}</td><td className="px-3 py-1.5"><span className={`text-xs border px-1.5 py-0.5 rounded ${TRAIN_BADGE[x.training_status] || ""}`}>{trainLabel(x.training_status)}</span></td><td className="px-3 py-1.5">{x.completed_date || "—"}</td><td className="px-3 py-1.5">{x.next_due || "—"}</td></tr>} />
      </Section>

      <Section title="R3 — Personnel Risk Assessment (PRA) Roster">
        <Tbl tid="cip004-rsaw-r3" head={["Name", "Type", "Status", "PRA", "Completed", "Valid until"]} rows={ev.r3_pra} empty="No personnel."
          render={(x) => <tr key={x.name} className="border-t border-border"><td className="px-3 py-1.5 font-medium">{x.name}</td><td className="px-3 py-1.5">{x.type}</td><td className="px-3 py-1.5 capitalize">{x.status}</td><td className="px-3 py-1.5"><span className={`text-xs border px-1.5 py-0.5 rounded ${PRA_BADGE[x.pra_status] || ""}`}>{praLabel(x.pra_status)}</span></td><td className="px-3 py-1.5">{x.completed_date || "—"}</td><td className="px-3 py-1.5">{x.valid_until || "—"}</td></tr>} />
      </Section>

      <Section title="R4.1 — Access Authorizations">
        <Tbl tid="cip004-rsaw-r41" head={["Subject", "Access", "Approver", "Source", "Reason", "Date"]} rows={ev.r41_authorizations} empty="No access authorizations."
          render={(x) => <tr key={`${x.subject}|${x.date}|${x.kinds}`} className="border-t border-border"><td className="px-3 py-1.5 font-medium">{x.subject}</td><td className="px-3 py-1.5">{x.kinds}</td><td className="px-3 py-1.5">{x.approver || "—"}</td><td className="px-3 py-1.5 capitalize">{x.source}</td><td className="px-3 py-1.5 max-w-xs truncate" title={x.business_reason}>{x.business_reason}</td><td className="px-3 py-1.5 whitespace-nowrap">{x.date}</td></tr>} />
      </Section>

      <Section title="R4.2 / R4.3 / R4.4 — Verification Campaigns">
        <Tbl tid="cip004-rsaw-r4v" head={["Campaign", "Citation", "Period", "Status", "Reviewed", "Discrepancies", "Completed"]} rows={ev.r4_verifications} empty="No verification campaigns."
          render={(x) => <tr key={`${x.type_label}|${x.period}`} className="border-t border-border"><td className="px-3 py-1.5 font-medium">{x.type_label}</td><td className="px-3 py-1.5 text-xs text-muted-foreground">{x.citation}</td><td className="px-3 py-1.5">{x.period}</td><td className="px-3 py-1.5 capitalize">{x.status}</td><td className="px-3 py-1.5">{x.reviewed}/{x.total}</td><td className="px-3 py-1.5">{x.discrepancies}</td><td className="px-3 py-1.5">{x.completed_at || "—"}</td></tr>} />
      </Section>

      <Section title="R5 — Access Revocation SLA">
        <div className="px-3 py-2 text-sm border-b border-border bg-secondary/20">
          Total {m.total || 0} · On time {m.completed_on_time || 0} · Late {m.completed_late || 0} · Open overdue <span className={m.open_overdue ? "text-red-600 font-semibold" : ""}>{m.open_overdue || 0}</span> · On-time rate <b>{m.on_time_pct != null ? `${m.on_time_pct}%` : "N/A"}</b>
        </div>
        <Tbl tid="cip004-rsaw-r5" head={["Person", "Task", "Citation", "Trigger", "Initiated", "Due", "SLA"]} rows={ev.r5_revocations} empty="No revocation tasks."
          render={(x) => <tr key={`${x.person}|${x.task}|${x.due_at}`} className="border-t border-border"><td className="px-3 py-1.5 font-medium">{x.person}</td><td className="px-3 py-1.5">{x.task}</td><td className="px-3 py-1.5 text-xs text-muted-foreground">{x.citation}</td><td className="px-3 py-1.5 capitalize">{x.trigger}</td><td className="px-3 py-1.5 whitespace-nowrap text-xs">{x.initiated_at}</td><td className="px-3 py-1.5 whitespace-nowrap text-xs">{x.due_at}</td><td className="px-3 py-1.5"><span className={`text-xs border px-1.5 py-0.5 rounded ${SLA_BADGE[x.sla_state] || ""}`}>{slaLabel(x.sla_state)}</span></td></tr>} />
      </Section>
    </div>
  );
}

// ---------------- External-manager request link (Phase 4) ----------------
function RequestLinkModal({ person, users, onClose }) {
  const [owner, setOwner] = useState(person.access_link?.asset_owner_user || "");
  const [link, setLink] = useState(person.access_link ? { url: `${window.location.origin}/access-request/${person.access_link.token}`, expires_at: person.access_link.expires_at } : null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cip004/people/${person.id}/request-link`, { asset_owner_user: owner }); setLink(data); toast.success("Link generated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    try { await api.delete(`/cip004/people/${person.id}/request-link`); setLink(null); toast.success("Link revoked"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard?.writeText(link.url); toast.success("Copied to clipboard"); };

  return (
    <Modal title={`External Access-Request Link — ${person.name}`} onClose={onClose}>
      <p className="text-sm text-muted-foreground mb-3">Share a secure link with an external/non-ClaroShield manager so they can request access <b>for {person.name} only</b>. Submissions route to the chosen approver and still enforce the PRA gate.</p>
      <Field label="Route to approver (asset owner)">
        <select className={inputCls} value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="cip004-link-owner"><option value="">—</option>{users.map((u) => <option key={u.id} value={u.name}>{u.name} ({u.role})</option>)}</select>
      </Field>
      {!link ? (
        <Button size="sm" className="gap-1" onClick={generate} disabled={busy} data-testid="cip004-link-generate"><LinkSimple size={14} /> {busy ? "Generating…" : "Generate link"}</Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input readOnly className={`${inputCls} font-mono text-xs`} value={link.url} data-testid="cip004-link-url" onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="outline" className="gap-1" onClick={copy} data-testid="cip004-link-copy"><Copy size={14} /></Button>
          </div>
          <div className="text-xs text-muted-foreground">Expires {(link.expires_at || "").slice(0, 10)}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="cip004-link-regenerate">Regenerate</Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={revoke} data-testid="cip004-link-revoke">Revoke</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}


// ---------------- Training drawer (CIP-004-7 R2) ----------------
function TrainingDrawer({ person, onClose, onChanged }) {
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get(`/cip004/people/${person.id}/training`).then((r) => { setRecords(r.data.records); setStatus(r.data.training_status); }).catch(() => {}), [person.id]);
  useEffect(() => { load(); }, [load]);
  const del = async (rid) => {
    if (!window.confirm("Delete this training record?")) return;
    try { await api.delete(`/cip004/training/${rid}`); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal title={`Security Training — ${person.name}`} onClose={onClose}>
      {status && (
        <div className="mb-3 text-sm">
          Current status: <span className={`text-xs border px-1.5 py-0.5 rounded ${TRAIN_BADGE[status.status]}`}>{trainLabel(status.status)}</span>
          {status.next_due && <> · next due <b>{status.next_due}</b></>}
        </div>
      )}
      {!adding && <Button size="sm" className="gap-1 mb-3" onClick={() => setAdding(true)} data-testid="cip004-add-train-btn"><Plus size={14} /> Record Training</Button>}
      {adding && <TrainingForm personId={person.id} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); onChanged(); }} />}
      <div className="border border-border divide-y divide-border" data-testid="cip004-train-records">
        {records.length === 0 ? <div className="px-3 py-4 text-sm text-muted-foreground">No training records.</div> : records.map((r) => (
          <div key={r.id} className="px-3 py-2 text-sm flex items-start justify-between gap-2">
            <div>
              <div className="font-medium">{r.completed_date || "—"} · {r.training_type}</div>
              <div className="text-xs text-muted-foreground">Next due {r.next_due || "N/A"}{r.notes ? ` · ${r.notes}` : ""}</div>
            </div>
            <button className="text-muted-foreground hover:text-red-600" onClick={() => del(r.id)} data-testid={`cip004-train-del-${r.id}`}><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function TrainingForm({ personId, onClose, onSaved }) {
  const [f, setF] = useState({ person_id: personId, completed_date: "", training_type: "Cyber Security Training", notes: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.completed_date) return toast.error("Completed date is required");
    try { await api.post("/cip004/training", f); toast.success("Training recorded"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-border p-3 mb-3 bg-secondary/30">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Completion date"><input type="date" className={inputCls} value={f.completed_date} onChange={(e) => set("completed_date", e.target.value)} data-testid="cip004-train-date" /></Field>
        <Field label="Training type">
          <select className={inputCls} value={f.training_type} onChange={(e) => set("training_type", e.target.value)} data-testid="cip004-train-type">
            <option>Cyber Security Training</option><option>Security Awareness</option>
          </select>
        </Field>
      </div>
      <Field label="Notes"><input className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g., LMS course ID" /></Field>
      <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} data-testid="cip004-train-save">Save</Button></div>
    </div>
  );
}

function TrainingPolicyModal({ onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [hook, setHook] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/cip004/training-policy").then((r) => setP(r.data)).catch(() => {});
    api.get("/cip004/training/webhook-info").then((r) => setHook(r.data)).catch(() => {});
  }, []);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter an email address");
    setSending(true);
    try { const { data } = await api.post("/cip004/training/test-email", { to: testTo }); toast.success(`Test email sent to ${data.to}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const save = async () => {
    try { await api.put("/cip004/training-policy", p); toast.success("Training policy saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rotate = async () => {
    if (!window.confirm("Rotate the webhook secret? The old secret will stop working.")) return;
    try { const { data } = await api.post("/cip004/training/webhook-info/rotate"); setHook((h) => ({ ...h, secret: data.secret })); toast.success("Secret rotated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!p) return <Modal title="Training Enforcement Policy" onClose={onClose}><div className="text-sm text-muted-foreground">Loading…</div></Modal>;
  return (
    <Modal title="Training Enforcement Policy (R2)" onClose={onClose} wide>
      <p className="text-sm text-muted-foreground mb-3">Choose how the annual training due date is enforced across the entity. Reminders (in-app + email to person, Cc manager) go out 30 days before due, at due, escalate to the manager 2 weeks after, and revoke access 2 weeks after that.</p>
      <Field label="Enforcement mode">
        <select className={inputCls} value={p.mode} onChange={(e) => set("mode", e.target.value)} data-testid="cip004-policy-mode">
          <option value="anniversary">Anniversary of each person's last training</option>
          <option value="fixed_date">Fixed reset date for everyone</option>
        </select>
      </Field>
      {p.mode === "fixed_date" && (
        <Field label="Fixed reset date (MM-DD)"><input className={inputCls} value={p.fixed_month_day} onChange={(e) => set("fixed_month_day", e.target.value)} placeholder="02-28" data-testid="cip004-policy-fixed" /></Field>
      )}
      <Field label="Cadence (months)"><input type="number" className={inputCls} value={p.cadence_months} onChange={(e) => set("cadence_months", parseInt(e.target.value || "12", 10))} data-testid="cip004-policy-cadence" /></Field>
      <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={!!p.digest_enabled} onChange={(e) => set("digest_enabled", e.target.checked)} data-testid="cip004-policy-digest" /> Send managers a weekly team digest instead of per-person emails</label>
      {p.digest_enabled && (
        <Field label="Digest day">
          <select className={inputCls} value={p.digest_day} onChange={(e) => set("digest_day", parseInt(e.target.value, 10))} data-testid="cip004-policy-digest-day">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </Field>
      )}
      <div className="flex justify-end gap-2 mb-4"><Button variant="outline" size="sm" onClick={onClose}>Cancel</Button><Button size="sm" onClick={save} data-testid="cip004-policy-save">Save policy</Button></div>

      <div className="border-t border-border pt-3 mb-4" data-testid="cip004-test-email">
        <div className="font-semibold text-sm mb-1">Test email delivery</div>
        <p className="text-xs text-muted-foreground mb-2">Send a test message to confirm Resend delivery (and that your sender domain is verified).</p>
        <div className="flex items-center gap-2">
          <input className={inputCls} type="email" placeholder="you@company.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="cip004-test-email-input" />
          <Button size="sm" variant="outline" className="gap-1 whitespace-nowrap" onClick={sendTest} disabled={sending} data-testid="cip004-test-email-btn">{sending ? "Sending…" : "Send test"}</Button>
        </div>
      </div>

      {hook && (
        <div className="border-t border-border pt-3" data-testid="cip004-webhook-info">
          <div className="font-semibold text-sm mb-1">LMS inbound webhook</div>
          <p className="text-xs text-muted-foreground mb-2">Have your LMS POST training completions to this URL with header <code>{hook.header}</code>. Body: <code>{`{email, completed_date, training_type}`}</code> (or <code>{`{records:[…]}`}</code>).</p>
          <div className="text-xs mb-1"><span className="text-muted-foreground">URL:</span> <span className="font-mono break-all">{hook.url}</span></div>
          <div className="flex items-center gap-2">
            <input readOnly className={`${inputCls} font-mono text-xs`} value={hook.secret} data-testid="cip004-webhook-secret" onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(hook.secret); toast.success("Secret copied"); }} data-testid="cip004-webhook-copy"><Copy size={14} /></Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={rotate} data-testid="cip004-webhook-rotate">Rotate</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

